import { useEffect, useMemo, useRef } from 'react';
import { loadExternalValue } from '@/lib/lexicalExternalValue';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { HeadingNode, QuoteNode } from '@lexical/rich-text';
import { TableCellNode, TableNode, TableRowNode } from '@lexical/table';
import { $createParagraphNode, $getRoot } from 'lexical';
import { ListItemNode, ListNode } from '@lexical/list';
import { AutoLinkNode, LinkNode } from '@lexical/link';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { CheckListPlugin } from '@lexical/react/LexicalCheckListPlugin';
import { TablePlugin } from '@lexical/react/LexicalTablePlugin';
import { HorizontalRulePlugin } from '@lexical/react/LexicalHorizontalRulePlugin';
import { HorizontalRuleNode } from '@lexical/react/LexicalHorizontalRuleNode';
import { MarkdownShortcutPlugin } from '@lexical/react/LexicalMarkdownShortcutPlugin';
import {
  $convertFromMarkdownString,
  $convertToMarkdownString,
  CHECK_LIST,
  TRANSFORMERS,
} from '@lexical/markdown';
import type { Transformer } from '@lexical/markdown';
import { CodeNode } from '@lexical/code';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';

// Import these from the memory bank note editor to reuse the theme and plugins
import NoteEditorTheme from '../memory-bank/lexical/NoteEditorTheme';
import { ToolbarPlugin } from '../memory-bank/lexical/plugins/ToolbarPlugin';
import { ListMaxIndentLevelPlugin } from '../memory-bank/lexical/plugins/ListMaxIndentLevelPlugin';
import { AutoLinkPlugin } from '../memory-bank/lexical/plugins/AutoLinkPlugin';
import {
  OnChangeMarkdown,
  INIT_RELOAD_TAG,
} from '../memory-bank/lexical/plugins/OnChangeMarkdown';
import { KeyboardShortcutsPlugin } from '../memory-bank/lexical/plugins/KeyboardShortcutsPlugin';
import { TabListIndentPlugin } from '../memory-bank/lexical/plugins/TabListIndentPlugin';
import { SlashCommandPlugin } from '../memory-bank/lexical/plugins/SlashCommandPlugin';
import {
  SanitizeLinksPlugin,
  $sanitizeLinks,
} from '../memory-bank/lexical/plugins/SanitizeLinksPlugin';
import '../memory-bank/lexical/lexical-editor.css';

import { MentionNode } from './lexical-mentions/MentionNode';
import { MentionPlugin } from './lexical-mentions/MentionPlugin';
import { MentionSourceContext } from './lexical-mentions/MentionContext';
import { createMentionTransformer } from './lexical-mentions/MentionTransformer';
import type { MentionSource } from './lexical-mentions/types';
import { BLOCK_TRANSFORMERS } from './lexical-markdown/markdownTransformers';

export interface MarkdownWYSIWYGEditorProps {
  initialValue?: string;
  onChange: (content: string) => void;
  placeholder?: string;
  height?: number;
  showToolbar?: boolean;
  readonly?: boolean;
  /** Optional mention sources. Pass one per entity type (e.g. tools, users). */
  mentionSources?: MentionSource[];
}

function Placeholder({ placeholder }: { placeholder: string }) {
  return (
    <div className="absolute top-3 left-3 text-muted-foreground pointer-events-none select-none">
      {placeholder}
    </div>
  );
}

// Always include MentionNode so it can be serialised / deserialised even
// when no sources are provided (keeps the registered node set static).
const BASE_NODES = [
  HeadingNode,
  ListNode,
  ListItemNode,
  QuoteNode,
  TableNode,
  TableCellNode,
  TableRowNode,
  AutoLinkNode,
  LinkNode,
  CodeNode,
  HorizontalRuleNode,
  MentionNode,
];

// Stable default so the no-mentions case doesn't hand a fresh array to the
// transformer memo / context provider on every render.
const NO_MENTION_SOURCES: MentionSource[] = [];

/**
 * Legacy / HTML-origin content sometimes arrives with stray `<br>` tags and
 * HTML entities. Strip them so the markdown converter sees clean source.
 */
function normalizeIncomingMarkdown(raw: string): string {
  let str = (raw || '').replace(/\n\n<br>\n/g, '\n');

  // If we still have br tags, clean them up
  if (str.match(/<br>/g)) {
    str = str.replace(/^(\n)(?=\s*[-+\d.])/gm, '').replace(/<br>/g, '');
  }

  return (
    str
      // Unescape HTML characters
      .replace(/&quot;/g, '"')
      .replace(/&amp;/g, '&')
      .replace(/&#39;/g, "'")
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
  );
}

/**
 * Replace the editor's entire content with the given markdown. Must run inside
 * an editor update (or the `editorState` initializer). An empty string yields a
 * single empty paragraph so focus behaves correctly.
 */
function $loadMarkdown(markdown: string, transformers: Transformer[]): void {
  const root = $getRoot();
  root.clear();
  if (!markdown) {
    root.append($createParagraphNode());
    return;
  }
  $convertFromMarkdownString(markdown, transformers);
  // The initial-load path runs before SanitizeLinksPlugin's transform is
  // registered, so neutralize any unsafe links the markdown introduced here.
  $sanitizeLinks(root);
}

/**
 * Keeps the editor's editable state in sync with the `readonly` prop. Lexical
 * only reads `editable` from `initialConfig` once, so without this a `readonly`
 * toggle after mount would have no effect.
 */
function EditablePlugin({ editable }: { editable: boolean }) {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    editor.setEditable(editable);
  }, [editor, editable]);

  return null;
}

/**
 * Re-loads content when `initialValue` changes after mount (e.g. a dialog that
 * fetches its record asynchronously). The mount value is already applied via
 * `initialConfig.editorState`, so the first run is a no-op; only genuinely new
 * values trigger a reload, which avoids clobbering in-progress edits.
 *
 * Many callers wire this editor as a *controlled* input — feeding `onChange`
 * output straight back into `initialValue`. Without a guard, every keystroke
 * would round-trip (type → onChange → new `initialValue` → reload) and each
 * reload clears + rebuilds the document, snapping the caret to the start. So
 * before reloading we compare the incoming value against the editor's current
 * serialized markdown; when they match (the controlled echo) we skip the reload
 * entirely and only a genuinely different external value rebuilds the content.
 */
function InitMarkdownPlugin({
  initialValue,
  transformers,
}: {
  initialValue: string;
  transformers: Transformer[];
}) {
  const [editor] = useLexicalComposerContext();
  const appliedValueRef = useRef(initialValue);

  useEffect(() => {
    if (initialValue === appliedValueRef.current) return;
    appliedValueRef.current = initialValue;

    // If the incoming value is just the editor's own emitted markdown echoed
    // back by a controlled parent, the content already matches — reloading it
    // would reset the caret for no reason, so skip it. Compare against the
    // *normalized* value (what a reload actually loads), not the raw
    // initialValue, so an HTML-origin echo with stray <br>/entities still
    // matches. Uses the same transformers as OnChangeMarkdown so the
    // serialization compares equal.
    const normalized = normalizeIncomingMarkdown(initialValue);
    const currentMarkdown = editor
      .getEditorState()
      .read(() => $convertToMarkdownString(transformers));
    if (normalized === currentMarkdown) return;

    // Kept out of the editor's undo, which starts afresh, and never moves focus
    // in; INIT_RELOAD_TAG tells OnChangeMarkdown not to echo this programmatic
    // load back as a user edit.
    loadExternalValue(editor, () => $loadMarkdown(normalized, transformers), [
      'history-merge',
      INIT_RELOAD_TAG,
    ]);
  }, [editor, initialValue, transformers]);

  return null;
}

export function MarkdownWYSIWYGEditor({
  initialValue = '',
  readonly = false,
  placeholder = 'Write your note here...',
  height = 200,
  showToolbar = true,
  onChange,
  mentionSources = NO_MENTION_SOURCES,
}: MarkdownWYSIWYGEditorProps) {
  const mentionTransformer = useMemo(
    () => createMentionTransformer(mentionSources),
    [mentionSources],
  );
  const allTransformers = useMemo(
    // Order matters: CHECK_LIST must precede the default unordered-list
    // transformer (both match a leading "- "), and the custom block
    // transformers (table, horizontal rule) run before the defaults too.
    () => [
      CHECK_LIST,
      ...BLOCK_TRANSFORMERS,
      ...TRANSFORMERS,
      mentionTransformer,
    ],
    [mentionTransformer],
  );

  return (
    <MentionSourceContext.Provider value={mentionSources}>
      <LexicalComposer
        initialConfig={{
          namespace: 'markdown-wysiwyg-editor',
          // Lexical controls editability here — NOT via a `readOnly` attribute
          // on ContentEditable (that spreads onto a <div> as a no-op).
          editable: !readonly,
          theme: NoteEditorTheme,
          onError(error: Error) {
            console.error('Lexical editor error:', error);
          },
          nodes: BASE_NODES,
          editorState: () => {
            $loadMarkdown(
              normalizeIncomingMarkdown(initialValue),
              allTransformers,
            );
          },
        }}
      >
        <div className="overflow-hidden rounded-md border border-input bg-background transition-colors focus-within:border-ring focus-within:ring-[3px] focus-within:ring-ring/30">
          {/* Hide the toolbar when readonly: its buttons mutate via commands
              that `editable={false}` does not block, so a read-only editor must
              not expose them at all. */}
          {showToolbar && !readonly && (
            <ToolbarPlugin
              features={{
                textColor: false,
                fontFamily: false,
                fontSize: false,
                lineSpacing: false,
                link: true,
                strikethrough: true,
                inlineCode: true,
                codeBlock: true,
                checklist: true,
                table: true,
                horizontalRule: true,
              }}
            />
          )}
          <div className="relative" style={{ minHeight: height }}>
            <RichTextPlugin
              contentEditable={
                <ContentEditable
                  aria-label={placeholder}
                  className="lexical-editor outline-none resize-none p-3 text-sm leading-relaxed"
                  style={{ minHeight: height }}
                />
              }
              placeholder={<Placeholder placeholder={placeholder} />}
              ErrorBoundary={LexicalErrorBoundary}
            />
          </div>
        </div>

        <HistoryPlugin />
        <ListPlugin />
        <CheckListPlugin />
        <LinkPlugin />
        <SanitizeLinksPlugin />
        <TablePlugin />
        <HorizontalRulePlugin />
        <OnChangeMarkdown onChange={onChange} transformers={allTransformers} />
        <AutoLinkPlugin />
        <ListMaxIndentLevelPlugin maxDepth={7} />
        <MarkdownShortcutPlugin transformers={allTransformers} />
        {/* Markdown has no syntax for underline, so disable Cmd/Ctrl+U here. */}
        <KeyboardShortcutsPlugin allowUnderline={false} />
        {/* Editing-only plugins are gated off in readonly mode so they can't
            mutate content the user isn't allowed to change. */}
        {!readonly && (
          <>
            {/* Tab indents inside lists only — never traps focus elsewhere. */}
            <TabListIndentPlugin />
            <SlashCommandPlugin
              features={{
                checklist: true,
                codeBlock: true,
                table: true,
                horizontalRule: true,
              }}
            />
          </>
        )}
        <EditablePlugin editable={!readonly} />
        <InitMarkdownPlugin
          initialValue={initialValue}
          transformers={allTransformers}
        />
        {mentionSources.length > 0 && (
          <MentionPlugin sources={mentionSources} />
        )}
      </LexicalComposer>
    </MentionSourceContext.Provider>
  );
}
